import type { FunctionalComponent as FC } from 'preact';
import { useCallback } from 'preact/hooks';

import { Icon20Clear } from '@vkontakte/icons';
import { Button, EllipsisText, Subhead } from '@vkontakte/vkui';
import { clsx } from 'clsx';

import { useWidget } from '../../context/WidgetContext';
import { CheckResult, type UploadedFileType } from '../../types/files';
import { getFileIcon, getFileType } from '../../utils';

import { RetryFileButton } from '../RetryFileButton';

import styles from './styles.module.css';

interface Props extends UploadedFileType {
  className?: string;
  onRemove: () => void;
  onRetryFileUpload: (fileName: string) => void;
  status?: 'fulfilled' | 'rejected';
}

export const AttachFile: FC<Props> = ({ className, name, status, type, uploadStatus, onRemove, onRetryFileUpload }) => {
  const { localeMessages } = useWidget();
  const fileType = getFileType(type);
  const fileIcon = getFileIcon(fileType);
  const isFileRejected = status === 'rejected';

  // В этих статусах считаем, что файл загружен без ошибок
  const fileIsOk = uploadStatus === CheckResult.Passed || uploadStatus === CheckResult.NotChecked;
  const errorText = !fileIsOk ? localeMessages.attach.fileError : undefined;

  // Повторная загрузка файла
  const handleRetryUpload = useCallback(() => onRetryFileUpload(name), [name, onRetryFileUpload]);

  return (
    <div className={clsx(styles.attachFile, className)}>
      {!fileIsOk ? (
        <div className={styles.fileOverlay}>
          {isFileRejected ? <RetryFileButton className={styles.retryButton} onClick={handleRetryUpload} /> : null}
          <Subhead className={styles.errorText}>{errorText}</Subhead>
        </div>
      ) : null}
      <Button
        before={<Icon20Clear />}
        className={styles.clear}
        mode="tertiary"
        appearance="neutral"
        onClick={onRemove}
      />
      <div className={styles.icon}>{fileIcon}</div>
      <EllipsisText maxLines={2}>
        <Subhead className={styles.fileName}>{name}</Subhead>
      </EllipsisText>
    </div>
  );
};
